import React, { useState, useEffect, useMemo } from 'react';
import QuestionResponse from 'models/QuestionResponse';
import { FrameProps } from './Multipart';

import 'view/style/student/components/newcomer/orderingFrame.css';
import QuestionFeedback from './QuestionFeedback';

const OrderingFrame: React.FC<FrameProps> = ({
  question,
  response,
  teacherView = false,
  showFeedback = true,
  isEvaluation = false,
  updateResponse,
}) => {
  const { choices = [] } = question;

  // compute the correct order of IDs
  const correctOrder = [...choices]
    .sort((a, b) => a.index - b.index)
    .map((c) => c.id);

  // state holds the ID (number) of each dropped choice, or null
  const [order, setOrder] = useState<(number | null)[]>(
    Array(choices.length).fill(null),
  );

  // allow dropping into blank or occupied slots
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  // drop into slot: handles blank insertion or pushing existing item
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, idx: number) => {
    if (teacherView) return;
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    const choiceId = parseInt(raw, 10);
    if (isNaN(choiceId)) return;

    // determine drop side to decide push direction
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const dir = offsetX < rect.width / 2 ? -1 : 1;

    setOrder((prev) => {
      const fromIndex = prev.findIndex((v) => v === choiceId);
      const existing = prev[idx];
      // remove dragged from its previous slot
      let next = prev.map((v) => (v === choiceId ? null : v));

      if (existing !== null) {
        // attempt to push existing one
        const pushIdx = idx + dir;
        if (pushIdx >= 0 && pushIdx < next.length && next[pushIdx] === null) {
          next[pushIdx] = existing;
        } else {
          // fallback: move existing back to original slot
          if (fromIndex >= 0) next[fromIndex] = existing;
        }
      }

      // place dragged choice
      next[idx] = choiceId;
      return next;
    });
  };

  // allow dragging placed choices back to unplaced list
  const handleReturnDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleReturnDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (teacherView) return;
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    const choiceId = parseInt(raw, 10);
    if (isNaN(choiceId)) return;

    setOrder((prev) => prev.map((v) => (v === choiceId ? null : v)));
  };

  // once all slots are filled, auto-grade
  useEffect(() => {
    if (
      !question.check ||
      order.some((v) => v == null) ||
      response.completed ||
      teacherView
    )
      return;

    const respText = (order as number[]).join(',');
    const isCorrect = JSON.stringify(order) === JSON.stringify(correctOrder);
    const grade = isCorrect ? 100 : 0;

    const updated = QuestionResponse.fromQuestionResponse(response);
    updated.response = respText;
    updated.grade = grade;
    updated.completed = true;

    const orderedText = choices
      .sort((c1, c2) => c1.index - c2.index)
      .map((c) => c.text)
      .join(' ');
    updated.feedback = isCorrect
      ? `You got it! ${orderedText}`
      : `Good try! ${orderedText}`;

    updateResponse(updated, false);
  }, [
    order,
    choices,
    correctOrder,
    response,
    showFeedback,
    updateResponse,
    question.check,
    teacherView,
  ]);

  const handleGradeResponse = (grade: number, feedback: string) => {
    if (!teacherView) return;

    const updatedResponse = QuestionResponse.fromQuestionResponse(response);
    updatedResponse.feedback = feedback;
    updatedResponse.grade = grade;

    updateResponse(updatedResponse);
  };

  // filter out any choices whose ID shows up in order
  const unplaced = useMemo(
    () => choices.filter((c) => !order.includes(Number(c.id))),
    [choices, order],
  );

  return (
    <div className="conversation-frame">
      <label className="label-normal">Put each phrase in the right spot</label>

      <div className="ordering-blanks-container">
        {order.map((slot, idx) => {
          const isBlank = slot === null;
          const SlotComp = (
            <div
              key={`slot-${idx}`}
              className={isBlank ? 'blank-slot' : 'option-slot'}
              draggable={!isBlank}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, idx)}
              onDragStart={
                !isBlank
                  ? (e) =>
                      e.dataTransfer.setData(
                        'text/plain',
                        slot?.toString() ?? '',
                      )
                  : undefined
              }
            >
              {isBlank ? (
                <span className="label-small">Drop here</span>
              ) : (
                <span className="label-normal">
                  {
                    choices.find((c) => c.id.toString() === slot.toString())
                      ?.text
                  }
                </span>
              )}
            </div>
          );
          return SlotComp;
        })}
      </div>

      {(teacherView ||
        (!teacherView && response.completed && showFeedback)) && (
        <QuestionFeedback
          feedback={response.feedback ?? ''}
          grade={response.grade}
          teacherView={teacherView}
          onGradeResponse={handleGradeResponse}
        />
      )}

      {/* Unplaced choices container with drop target to return choices */}
      <div
        className="ordering-blanks-container"
        onDragOver={handleReturnDragOver}
        onDrop={handleReturnDrop}
      >
        {unplaced.map((c) => (
          <div
            key={c.id}
            className="option-slot"
            draggable
            onDragStart={(e) =>
              e.dataTransfer.setData('text/plain', c.id.toString())
            }
          >
            <span className="label-normal">{c.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderingFrame;
